import React, { useEffect, useState } from "react";
import { Bell, BellOff, Send, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  currentPushPermission,
  enablePush,
  disablePush,
  sendTestPush,
  isSubscribed,
} from "@/lib/push";

/**
 * Notifications settings block for the espace-client Profile page.
 * - Prompts the user to enable Web Push
 * - Allows disabling / sending a test
 */
export default function PushSettings() {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = pushSupported();
    setSupported(s);
    if (!s) return;
    setPerm(currentPushPermission());
    isSubscribed().then(setSubscribed).catch(() => {});
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setPerm("granted");
      setSubscribed(true);
      toast.success("Notifications activées.");
    } catch (e) {
      toast.error(e.message || "Impossible d'activer les notifications.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast.success("Notifications désactivées.");
    } catch {
      toast.error("Erreur lors de la désactivation.");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Notification test envoyée.");
    } catch {
      toast.error("Échec de l'envoi test.");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="card-luxe p-5" data-testid="push-settings-unsupported">
        <div className="flex items-start gap-3">
          <BellOff size={18} className="text-white/40 mt-0.5" />
          <div>
            <p className="text-sm text-white/70">
              Les notifications push ne sont pas supportées sur ce navigateur.
            </p>
            <p className="text-xs text-white/45 mt-1">
              Essayez Chrome, Firefox ou Edge sur ordinateur, ou installez l&apos;application (PWA) sur mobile.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-luxe p-5" data-testid="push-settings">
      <div className="flex items-start gap-3 mb-4">
        <Bell size={18} className="text-[#DCB457] mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1">Notifications</p>
          <p className="text-sm text-white/80">
            Recevez les rappels J-7 et J-1 avant votre événement, les confirmations d&apos;acompte et les nouveautés importantes.
          </p>
        </div>
      </div>

      {perm === "denied" ? (
        <div className="flex items-start gap-2 text-xs text-rose-300/90 bg-rose-500/[0.05] border border-rose-500/20 p-3 mb-3">
          <X size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Vous avez bloqué les notifications pour ce site. Réactivez-les dans les paramètres de votre navigateur (icône cadenas dans la barre d&apos;adresse).
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {!subscribed ? (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy || perm === "denied"}
            className="btn-gold text-sm inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="push-enable-btn"
          >
            <Bell size={14} /> Activer les notifications
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleTest}
              disabled={busy}
              className="btn-ghost-gold text-sm inline-flex items-center gap-2"
              data-testid="push-test-btn"
            >
              <Send size={14} /> Envoyer un test
            </button>
            <button
              type="button"
              onClick={handleDisable}
              disabled={busy}
              className="text-sm inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/70 hover:bg-white/5"
              data-testid="push-disable-btn"
            >
              <BellOff size={14} /> Désactiver
            </button>
          </>
        )}
      </div>
    </div>
  );
}
